"use client";

import { useCallback, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { authenticatedFetch } from "@/utils/api";
import {
  buildActivityEvent,
  getElementActivityLabel,
  getElementActivityRole,
  subscribeToActivityEvents,
  type ClientActivityEvent,
} from "@/lib/activityTracking";

const FLUSH_INTERVAL_MS = 10_000;
const MAX_BATCH_SIZE = 20;
const MIN_VIEW_DURATION_MS = 1_000;

function formatDuration(durationMs: number) {
  const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

export function AuthenticatedActivityTracker() {
  const { user } = useAuth();
  const pathname = usePathname() || "/";
  const previousPathnameRef = useRef<string | null>(null);
  const queueRef = useRef<ClientActivityEvent[]>([]);
  const flushInFlightRef = useRef(false);
  const activeViewRef = useRef<{ route: string; startedAt: number } | null>(null);

  const flush = useCallback(async () => {
    if (!user || flushInFlightRef.current || queueRef.current.length === 0) return;

    const events = queueRef.current.splice(0, MAX_BATCH_SIZE);
    flushInFlightRef.current = true;

    try {
      const response = await authenticatedFetch("/api/platform-events/client", {
        method: "POST",
        body: JSON.stringify({ events }),
      });
      if (response.status === 401 || response.status === 403) {
        queueRef.current = [];
        return;
      }
      if (!response.ok) {
        queueRef.current = [...events, ...queueRef.current].slice(0, MAX_BATCH_SIZE * 3);
      }
    } catch {
      queueRef.current = [...events, ...queueRef.current].slice(0, MAX_BATCH_SIZE * 3);
    } finally {
      flushInFlightRef.current = false;
    }
  }, [user]);

  const enqueue = useCallback(
    (event: ClientActivityEvent) => {
      if (!user) return;
      queueRef.current.push(event);
      if (queueRef.current.length >= MAX_BATCH_SIZE) {
        void flush();
      }
    },
    [flush, user],
  );

  const endActiveView = useCallback(
    (reason: "route_change" | "hidden" | "pagehide") => {
      const activeView = activeViewRef.current;
      if (!activeView) return;

      activeViewRef.current = null;
      const durationMs = Date.now() - activeView.startedAt;
      if (durationMs < MIN_VIEW_DURATION_MS) return;

      const durationSeconds = Math.round(durationMs / 1000);
      enqueue(
        buildActivityEvent(
          "app.view_duration",
          {
            route: activeView.route,
            durationMs,
            durationSeconds,
            reason,
          },
          `Viewed ${activeView.route} for ${formatDuration(durationMs)}`,
        ),
      );
    },
    [enqueue],
  );

  useEffect(() => {
    if (!user) return;

    const previousRoute = previousPathnameRef.current;
    if (activeViewRef.current?.route !== pathname) {
      endActiveView("route_change");
    }

    previousPathnameRef.current = pathname;
    activeViewRef.current = { route: pathname, startedAt: Date.now() };
    enqueue(
      buildActivityEvent(
        "app.page_view",
        { route: pathname, previousRoute },
        `Viewed ${pathname}`,
      ),
    );
    void flush();
  }, [endActiveView, enqueue, flush, pathname, user]);

  useEffect(() => {
    if (!user) return;

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const element = target?.closest<HTMLElement>(
        "button,a,[role='button'],[role='menuitem'],[role='tab'],[data-clickable],[data-activity-label]",
      );
      if (!element) return;
      if (element.closest("input,textarea,select,[contenteditable='true']")) return;

      const label = getElementActivityLabel(element);
      if (!label) return;

      enqueue(
        buildActivityEvent(
          "app.click",
          {
            route: pathname,
            label,
            elementRole: getElementActivityRole(element),
            href: element instanceof HTMLAnchorElement ? element.pathname : undefined,
          },
          `Clicked ${label}`,
        ),
      );
    };

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, [enqueue, pathname, user]);

  useEffect(() => {
    if (!user) return;
    return subscribeToActivityEvents(enqueue);
  }, [enqueue, user]);

  useEffect(() => {
    if (!user) return;

    const interval = window.setInterval(() => {
      void flush();
    }, FLUSH_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        endActiveView("hidden");
        void flush();
        return;
      }

      activeViewRef.current = { route: pathname, startedAt: Date.now() };
    };
    const onPageHide = () => {
      endActiveView("pagehide");
      void flush();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [endActiveView, flush, pathname, user]);

  return null;
}
